import { useRef, useState } from 'react';
import useTable from '@/hooks/useTable';
import { ErrorBlock, LoadingBlock } from '@/components/feature/SectionState';
import type { GsBulletin } from '@/lib/types';
import BulletinDetail from './BulletinDetail';
import BulletinCompose from './BulletinCompose';

const APPROVED_FILTER: Record<string, string> = { status: 'approved' };

export default function CommunityBulletin() {
  const { data: notices, loading, error, reload } = useTable<GsBulletin>('gs_bulletins', 'sort_order', APPROVED_FILTER);
  const [active, setActive] = useState<GsBulletin | null>(null);
  const [composing, setComposing] = useState(false);
  const railRef = useRef<HTMLDivElement>(null);
  const drag = useRef({ active: false, startX: 0, startScroll: 0, moved: false });

  const scrollBy = (dir: number) => {
    railRef.current?.scrollBy({ left: dir * 320, behavior: 'smooth' });
  };

  const onDragStart = (e: React.MouseEvent) => {
    const el = railRef.current;
    if (!el) return;
    drag.current = { active: true, startX: e.pageX, startScroll: el.scrollLeft, moved: false };
  };

  const onDragMove = (e: React.MouseEvent) => {
    const el = railRef.current;
    if (!el || !drag.current.active) return;
    const dx = e.pageX - drag.current.startX;
    if (Math.abs(dx) > 4) drag.current.moved = true;
    el.scrollLeft = drag.current.startScroll - dx;
  };

  const onDragEnd = () => {
    drag.current.active = false;
  };

  const onClickCapture = (e: React.MouseEvent) => {
    if (drag.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      drag.current.moved = false;
    }
  };

  return (
    <section id="bulletin" className="bg-background-100 border-y border-background-200 px-4 md:px-8 py-14 md:py-20">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <p className="font-label uppercase tracking-[0.28em] text-xs text-primary-600 font-semibold">Bulletin</p>
          <h2 className="font-heading font-bold text-3xl md:text-5xl text-foreground-950 leading-[1.05] mt-3">The Community Board</h2>
          <p className="mt-3 text-foreground-700 text-sm md:text-base max-w-xl">Short local notices from around the fairways — sign-ups, volunteer calls, lost-and-found and last-minute tee times. Tap any notice to read it in full.</p>
        </div>

        <div className="flex items-center gap-2 self-start md:self-end">
          <button
            type="button"
            onClick={() => setComposing(true)}
            className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 font-label font-bold uppercase tracking-wider text-xs md:text-sm px-4 md:px-5 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors"
          >
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-pushpin-2-line"></i></span>
            Pin your own notice
          </button>
          <div className="hidden md:flex items-center gap-2">
            <button type="button" onClick={() => scrollBy(-1)} aria-label="Scroll board left" className="w-10 h-10 rounded-full border border-background-300 bg-background-50 hover:border-primary-500 hover:text-primary-600 flex items-center justify-center cursor-pointer text-foreground-800 transition-colors">
              <i className="ri-arrow-left-line"></i>
            </button>
            <button type="button" onClick={() => scrollBy(1)} aria-label="Scroll board right" className="w-10 h-10 rounded-full border border-background-300 bg-background-50 hover:border-primary-500 hover:text-primary-600 flex items-center justify-center cursor-pointer text-foreground-800 transition-colors">
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </div>

      {loading && <LoadingBlock label="Loading the board" />}
      {error && <ErrorBlock message={error} onRetry={reload} />}

      {!loading && !error && (
        <div
          ref={railRef}
          onMouseDown={onDragStart}
          onMouseMove={onDragMove}
          onMouseUp={onDragEnd}
          onMouseLeave={onDragEnd}
          onClickCapture={onClickCapture}
          onDragStart={(e) => e.preventDefault()}
          className="gs-rail flex gap-5 overflow-x-auto -mx-1 px-1 pt-6 pb-8 cursor-grab active:cursor-grabbing select-none"
        >
          {notices.map((n, i) => (
            <button
              key={n.id}
              type="button"
              onClick={() => setActive(n)}
              style={{ animationDelay: `${i * 0.07}s` }}
              className="gs-rise shrink-0 group cursor-pointer text-left"
            >
              <div
                className={`relative w-[280px] md:w-[300px] h-full bg-background-50 border border-background-200 rounded-md p-5 pt-7 transition-all duration-300 ease-out group-hover:-translate-y-1.5 group-hover:border-primary-300 ${i % 2 === 0 ? 'rotate-[-1deg]' : 'rotate-[1deg]'} group-hover:rotate-0`}
              >
                {/* Thumb tack */}
                <span aria-hidden="true" className="absolute -top-2.5 left-1/2 -translate-x-1/2 w-5 h-5">
                  <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-3.5 h-2 rounded-full bg-foreground-950/15 blur-[2px]"></span>
                  <span className="absolute inset-0 rounded-full bg-gradient-to-br from-primary-300 via-primary-500 to-primary-800"></span>
                  <span className="absolute top-[3px] left-[5px] w-1.5 h-1.5 rounded-full bg-background-50/80"></span>
                </span>

                <span className="inline-block bg-primary-50 text-primary-700 font-label font-semibold uppercase text-[11px] tracking-[0.16em] px-2.5 py-1 rounded-sm">{n.tag}</span>
                <h3 className="mt-3 font-heading font-bold text-xl leading-snug text-foreground-950 group-hover:text-primary-600 transition-colors">{n.title}</h3>
                {n.body && <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-3">{n.body}</p>}
                {n.meta && (
                  <p className="mt-4 pt-3 border-t border-background-200 flex items-center gap-1.5 font-label uppercase tracking-widest text-[11px] text-foreground-500">
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-map-pin-line"></i></span>{n.meta}
                  </p>
                )}
                <p className="mt-3 inline-flex items-center gap-1.5 font-label uppercase tracking-widest text-[10px] font-semibold text-primary-600">
                  Read notice<span className="w-3.5 h-3.5 flex items-center justify-center transition-transform duration-300 group-hover:translate-x-0.5"><i className="ri-arrow-right-line"></i></span>
                </p>
              </div>
            </button>
          ))}

          {notices.length === 0 && (
            <p className="text-foreground-600 py-4">The board is empty right now — be the first to pin a notice.</p>
          )}
        </div>
      )}

      <BulletinDetail notice={active} onClose={() => setActive(null)} />
      <BulletinCompose open={composing} onClose={() => setComposing(false)} />
    </section>
  );
}